// src/pages/admin/AnalyticsReports.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IconBellRinging,
  IconCash,
  IconBox,
  IconUsers,
  IconChartPie,
  IconBan,
  IconArrowBackUp,
  IconCalendar,
  IconRefresh
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import Sidebar from "../../components/common/AdminSideBar";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { fetchAnalyticsAdmin } from "../../redux/slices/analyticsSlice";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import ErrorFallback from "../../components/common/ErrorFallback";
import LoadingFallback from "../../components/common/LoadingFallback";


function NavItem({ label, to }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-3 py-2 rounded-md cursor-pointer flex items-center gap-2
        ${isActive
          ? "bg-[#FFD500] text-[#0B0C52] font-semibold"
          : "hover:bg-white/10 text-white"
        }`}
    >
      {label}
    </Link>
  );
}

function AnalyticsReports() {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const { setLayoutConfig } = useAdminLayout();

  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Redux
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector((state) => state.analytics);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };

  // Fetch analytics when dates change or on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Analytics & Reports",
      showBackButton: false,
      scrollable: true,
    });
    dispatch(fetchAnalyticsAdmin({ startDate, endDate }));
  }, [dispatch, startDate, endDate, setLayoutConfig]);

  const handleRefresh = () => {
    dispatch(fetchAnalyticsAdmin({ startDate, endDate }));
  };

  // Format changes with +/-
  function formatChange(changeValue) {
    if (changeValue > 0) return `+${changeValue}%`;
    if (changeValue < 0) return `${changeValue}%`;
    return "0%";
  }

  // Determine color based on change value
  function changeColor(changeValue) {
    return changeValue >= 0 ? "text-green-500" : "text-red-500";
  }

  if (loading) {
    return <LoadingFallback/>
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={handleRefresh} />;
  }

  return (
    <div>
      <main className="p-6 flex-1 overflow-y-auto">
        <div className="border-2 border-[#463BC9] rounded-md bg-[#F9F9FF] p-4">
          {/* Date Range Selection */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-[#0B0C52]">
                {data?.dateRange?.current?.from} - {data?.dateRange?.current?.to}
              </h3>
              <p className="text-sm text-gray-500">
                Compared to {data?.dateRange?.previous?.from} - {data?.dateRange?.previous?.to}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <IconCalendar className="text-gray-500" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded text-sm px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#0B0C52]"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded text-sm px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#0B0C52]"
                />
              </div>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-1 text-sm bg-[#0B0C52] text-white px-3 py-1 rounded hover:bg-[#0B0C52]/90"
              >
                <IconRefresh size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* Analytics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Total Sales */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Sales</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    ${data?.totalSales?.amount?.toLocaleString() || 0}
                  </p>
                </div>
                <IconCash size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.totalSales?.change)}`}>
                {formatChange(data?.totalSales?.change)} from previous period
              </p>
            </div>

            {/* Best Selling Product */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Best Selling Product</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    {data?.bestSellingProduct?.name || "N/A"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {data?.bestSellingProduct?.category || ""}
                  </p>
                </div>
                <IconBox size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.bestSellingProduct?.change)}`}>
                {formatChange(data?.bestSellingProduct?.change)} from previous period
              </p>
            </div>

            {/* New Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">New Users</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    {data?.newUsers?.count?.toLocaleString() || 0}
                  </p>
                </div>
                <IconUsers size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.newUsers?.change)}`}>
                {formatChange(data?.newUsers?.change)} from previous period
              </p>
            </div>

            {/* Customer Retention */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Customer Retention</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    {data?.customerRetention?.rate || 0}%
                  </p>
                </div>
                <IconChartPie size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.customerRetention?.change)}`}>
                {formatChange(data?.customerRetention?.change)} from previous period
              </p>
            </div>

            {/* Canceled Orders */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Canceled Orders</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    {data?.canceledOrders?.count || 0}
                  </p>
                </div>
                <IconBan size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.canceledOrders?.change)}`}>
                {formatChange(data?.canceledOrders?.change)} from previous period
              </p>
            </div>

            {/* Returned Orders */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Returned Orders</p>
                  <p className="text-2xl font-bold text-[#0B0C52]">
                    {data?.returnedOrders?.count || 0}
                  </p>
                </div>
                <IconArrowBackUp size={32} className="text-[#0B0C52]" />
              </div>
              <p className={`mt-2 text-sm ${changeColor(data?.returnedOrders?.change)}`}>
                {formatChange(data?.returnedOrders?.change)} from previous period
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AnalyticsReports;